import React, { useState } from 'react';

import ModalConfirmacao from '../modal/confirmacao/ModalConfirmacao';
import { Botao, Chip, Icone, Nota } from '../../ui';

import { DivergenciaSga } from '../../api/sga-divergencia/sgaDivergenciaApi';
import { dispensarDivergencias, idsDispensados } from '../../utils/divergenciaDispensada';
import { valorOuTraco } from '../../utils/envioSgaRegras';

export interface AvisoDivergenciaSgaProps {
  divergencias: DivergenciaSga[];
  /** Some do DOM quando não há nada a avisar (o padrão). */
  dispensavel?: boolean;
  /**
   * SGA → local: aplica os valores do SGA no cadastro daqui. Omitir esconde o
   * botão (telas sem contexto de registro só exibem o aviso).
   */
  onTrazerDoSga?: () => Promise<void> | void;
  /**
   * Local → SGA: empurra o cadastro daqui para o SGA. Só existe para VEÍCULO —
   * a API do SGA não tem alteração cadastral de associado.
   */
  onEnviarAoSga?: () => Promise<void> | void;
  /** Trava os botões enquanto a ação está em andamento. */
  processando?: boolean;
}

/** Houve mudança de valor? (valorLocal presente e diferente do valor no SGA) */
const temMudanca = (d: DivergenciaSga): boolean => {
  const local = (d.valorLocal ?? '').trim();
  return local !== '' && local !== (d.valorSga ?? '').trim();
};

/**
 * Faixa de atenção: "este registro foi alterado no SGA" (at-32; reescrita na at-70,
 * frente D/D8).
 *
 * **Nunca bloqueia o fluxo** — o consultor decide se segue. É informação, não
 * trava: o SAAC precisa operar mesmo quando o SGA discorda ou está fora do ar.
 *
 * O texto de cada linha vem **pronto do backend** (`rotulo`, `descricao`,
 * `gravidade`): a regra de qual divergência é grave e o que fazer com ela vive na
 * whitelist de lá, não replicada aqui (convenção da at-24).
 *
 * ## O que mudou na at-70
 *
 *   1. **Virou `Nota variante="atencao"` + `Chip` + `Botao` do kit.** Ela tinha CSS
 *      próprio (`AvisoDivergenciaSga.css`) e importava `enviosSga.css` **só pelos
 *      dois botões** `.envios-acao-btn--secundario` — era o último importador
 *      daquele arquivo fora de `/ti/envios`, e o que impedia apagá-lo.
 *   2. 🔴 **Gravidade alta deixou de trocar a COR DA FAIXA INTEIRA** (de âmbar para
 *      vermelho) e virou um `Chip perigo` na linha que é grave. A faixa vermelha
 *      dizia "algo aqui é grave" sem dizer **o quê** — e um aviso que não bloqueia
 *      nada, pintado como alarme, é o que a spec 02 §2.3 pede para evitar. O título
 *      continua mudando ("Este registro foi encontrado diferente no SGA"), que é a
 *      parte informativa da distinção.
 *   3. **O `×` virou `Icone fechar`** com `aria-label` (era caractere literal).
 *
 * ⚠️ **As três telas que a renderizam não mudaram** — `/adesoes`,
 * `/carro/substituir` e `/vistoria/captura`, todas já migradas. O contrato de props
 * é o mesmo, e a dispensa por id (`utils/divergenciaDispensada`) continua idêntica:
 * fato novo no SGA = divergência nova = id novo, então o aviso volta sozinho quando
 * algo mudar de verdade. A fila do TI não é afetada.
 */
const AvisoDivergenciaSga: React.FC<AvisoDivergenciaSgaProps> = ({
  divergencias,
  dispensavel = true,
  onTrazerDoSga,
  onEnviarAoSga,
  processando = false,
}) => {
  const [dispensado, setDispensado] = useState(false);
  /** Qual confirmação está aberta — a ação só roda depois do "Confirmar". */
  const [confirmando, setConfirmando] = useState<'TRAZER' | 'ENVIAR' | null>(null);

  const vistas = idsDispensados();
  const visiveis = (divergencias ?? []).filter((d) => !vistas.has(d.id));

  if (!visiveis.length || dispensado) return null;

  const critico = visiveis.some((d) => d.gravidade === 'ALTA');

  // Campos que os botões conseguem sincronizar × estruturais (troca de titular,
  // substituição, carteira), que continuam sendo ação da equipe de TI. O
  // `aplicavel` vem PRONTO do backend, onde mora a whitelist.
  const aplicaveis = visiveis.filter((d) => d.aplicavel);
  const estruturais = visiveis.filter((d) => !d.aplicavel);

  return (
    <Nota variante="atencao" className="tw-mx-auto tw-mb-4 tw-max-w-4xl">
      <div className="tw-flex tw-items-start tw-gap-2">
        <strong className="tw-min-w-0 tw-flex-1 tw-text-sm tw-font-bold">
          {critico
            ? 'Este registro foi encontrado diferente no SGA'
            : 'Há diferenças entre este cadastro e o SGA'}
        </strong>

        {dispensavel && (
          <button
            type="button"
            aria-label="Dispensar aviso"
            className="tw-shrink-0 tw-cursor-pointer tw-rounded-lg tw-border-0 tw-bg-transparent tw-p-1 tw-text-current tw-opacity-70 hover:tw-opacity-100 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-amber-warn/50"
            onClick={() => {
              dispensarDivergencias(visiveis.map((d) => d.id));
              setDispensado(true);
            }}
          >
            <Icone nome="fechar" tamanho={16} />
          </button>
        )}
      </div>

      <ul className="tw-m-0 tw-mt-2 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
        {visiveis.map((divergencia) => (
          <li key={divergencia.id}>
            <span className="tw-font-semibold">{divergencia.rotulo}:</span>{' '}
            {/* A seta "local → SGA" só faz sentido quando houve MUDANÇA. Alertas de
                estado (ex.: situação não ativa no SGA) vêm sem valorLocal — ou com o
                mesmo valor, em registros antigos — e mostram só o valor do SGA
                (senão a tela dizia "TESTE DE SISTEMA → TESTE DE SISTEMA"). */}
            <span className="[overflow-wrap:anywhere]">
              {temMudanca(divergencia) ? (
                <>
                  <span className="tw-line-through tw-opacity-75">
                    {valorOuTraco(divergencia.valorLocal)}
                  </span>
                  <span aria-hidden="true">{' → '}</span>
                  <span className="tw-font-semibold">{valorOuTraco(divergencia.valorSga)}</span>
                </>
              ) : (
                <span className="tw-font-semibold">{valorOuTraco(divergencia.valorSga)}</span>
              )}
            </span>

            {divergencia.gravidade === 'ALTA' && (
              <>
                {' '}
                <Chip variante="perigo">Gravidade alta</Chip>
                <div className="tw-mt-0.5 tw-opacity-90">{divergencia.descricao}</div>
              </>
            )}
          </li>
        ))}
      </ul>

      {(onTrazerDoSga || onEnviarAoSga) && aplicaveis.length > 0 && (
        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row">
          {onTrazerDoSga && (
            <Botao
              variante="secundario"
              tamanho="sm"
              disabled={processando}
              onClick={() => setConfirmando('TRAZER')}
            >
              Trazer do SGA
            </Botao>
          )}
          {onEnviarAoSga && (
            <Botao
              variante="secundario"
              tamanho="sm"
              disabled={processando}
              onClick={() => setConfirmando('ENVIAR')}
            >
              Enviar ao SGA
            </Botao>
          )}
        </div>
      )}

      <div className="tw-mt-2.5 tw-opacity-90">
        {estruturais.length > 0 && (
          <div className="tw-mb-1">
            {estruturais.length === 1
              ? 'A diferença acima exige'
              : `${estruturais.length} das diferenças acima exigem`}{' '}
            ação da equipe de TI — ela aparece na aba “Divergências” do Acompanhamento de Envios com
            a orientação do caso.
          </div>
        )}
        {aplicaveis.length > 0 && !onTrazerDoSga && !onEnviarAoSga && (
          <>
            O cadastro local não foi alterado. A equipe de TI resolve essas diferenças na aba
            “Divergências” do Acompanhamento de Envios.
          </>
        )}
      </div>

      <ModalConfirmacao
        aberto={confirmando === 'TRAZER'}
        titulo="Trazer os dados do SGA?"
        mensagem={`${aplicaveis.length} campo(s) do cadastro local serão substituídos pelos valores do SGA.`}
        detalhe={aplicaveis.map((d) => d.rotulo).join(', ')}
        rotuloConfirmar="Trazer do SGA"
        processando={processando}
        onCancelar={() => setConfirmando(null)}
        onConfirmar={async () => {
          await onTrazerDoSga?.();
          setConfirmando(null);
        }}
      />

      <ModalConfirmacao
        aberto={confirmando === 'ENVIAR'}
        titulo="Enviar este cadastro ao SGA?"
        mensagem="Os dados cadastrados aqui vão substituir os que estão no SGA."
        detalhe={aplicaveis.map((d) => d.rotulo).join(', ')}
        rotuloConfirmar="Enviar ao SGA"
        processando={processando}
        onCancelar={() => setConfirmando(null)}
        onConfirmar={async () => {
          await onEnviarAoSga?.();
          setConfirmando(null);
        }}
      />
    </Nota>
  );
};

export default AvisoDivergenciaSga;
